import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js"
import { chartTooltipConfig } from "../../utils/chart_tooltip_config"

// Main palette (green, steel-blue, sand, olive, gray) for V2 charts
const CHART_COLORS = [
  "#4BB82E", "#509DA2", "#C89A6A", "#8A9849", "#7A7A6E",
  "#63DA3A", "#70B5B9", "#E4B88A", "#B0BB72", "#9A9A8C",
  "#389022", "#3E8388", "#A87B4E", "#6B7A2E", "#5C5C50"
]

export default class extends Controller {
  static values = {
    data: Array,
    legend: Array,
    type: { type: String, default: "multi" }
  }

  connect () {
    if (process.env.NODE_ENV !== "production") {
      console.debug("[investments-pie] connect", {
        data: this.dataValue,
        legend: this.legendValue,
        type: this.typeValue
      })
    }
    this.renderChart()
  }

  disconnect () {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  renderChart () {
    if (!this.hasDataValue || this.dataValue.length === 0) return

    const ctx = this.element.getContext("2d")
    if (!ctx) return

    if (this.chart) {
      this.chart.destroy()
    }

    const single = this.typeValue === "single"
    const { labels, values, colors } = single ? this.singleChartConfig() : this.multiChartConfig()

    this.chart = new Chart(ctx, {
      type: "pie",
      data: {
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderWidth: 0
          }
        ],
        labels
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        layout: {
          padding: 0
        },
        tooltips: chartTooltipConfig({
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || ""
              const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index]
              return `${label ? `${label}: ` : ""}${this.formatCurrency(value)}`
            }
          }
        })
      }
    })

    this.renderLegend(single)
  }

  singleChartConfig () {
    const labels = this.dataValue.map(entry => entry[0])
    const values = this.dataValue.map(entry => Number(entry[1]))
    const colors = this.dataValue.map(entry => entry[2])
    return { labels, values, colors }
  }

  multiChartConfig () {
    const labels = this.dataValue.map(entry => entry[0])
    const values = this.dataValue.map(entry => Number(entry[1]))
    const colors = CHART_COLORS.slice(0, values.length)
    return { labels, values, colors }
  }

  renderLegend (single) {
    const selector = single ? "[data-pie-legend-single]" : "[data-pie-legend]"
    const legendContainer = document.querySelector(selector)
    if (!legendContainer) return

    const legendRows = single
      ? this.legendValue || []
      : this.buildLegendFromChart()

    if (!legendRows || legendRows.length === 0) {
      legendContainer.innerHTML = ""
      return
    }

    const rowsHtml = legendRows.map(row => this.legendRowMarkup(row)).join("")
    legendContainer.innerHTML = `<div class="inline-grid grid-cols-[auto_auto] gap-x-4">${rowsHtml}</div>`
  }

  buildLegendFromChart () {
    if (!this.chart) return []
    return this.chart.data.labels.map((label, index) => [
      label,
      this.chart.data.datasets[0].data[index],
      this.chart.data.datasets[0].backgroundColor[index]
    ])
  }

  legendRowMarkup ([label, value, color]) {
    const formattedValue = typeof value === "number" ? this.formatCurrency(value) : value
    return `
      <div class="flex items-center gap-2 py-1">
        <div class="w-4 h-4 rounded-full shrink-0" style="background-color:${color}"></div>
        ${label}
      </div>
      <div class="py-1 text-right tabular-nums">${formattedValue}</div>
    `
  }

  formatCurrency (value) {
    const number = Number(value) || 0
    return `${number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`
  }
}

